import React, { useContext, useEffect, useState, useCallback, useMemo } from "react";
import { APIContext } from "../../../api";
import { Select } from "@chakra-ui/react";
import { PulseLoader } from "react-spinners";
import { toast } from "react-toastify";

/*
  AI website builder: which coding-agent CLI (and model) serves builds.

  The adapter list, the model lists, and the "is it usable" test all come from
  the builder itself through the admin API proxy — this screen never hard-codes
  a vendor's models. Saving runs a live health check first and refuses an
  unusable pick unless the admin forces it.
*/
function AIBuilderAdapter() {
  const {
    getAiBuilderAdapterState,
    getAiBuilderAdapterModels,
    validateAiBuilderAdapter,
    saveAiBuilderAdapter,
  } = useContext(APIContext);

  const [isLoading, setIsLoading] = useState(true);
  const [adapters, setAdapters] = useState([]); // [{id,label,models:[]}]
  const [engine, setEngine] = useState("");
  const [activeAdapter, setActiveAdapter] = useState("");
  const [activeModel, setActiveModel] = useState("");

  const [activeEffort, setActiveEffort] = useState("");

  const [adapter, setAdapter] = useState("");
  const [model, setModel] = useState("");
  const [effort, setEffort] = useState("");
  const [models, setModels] = useState([]);
  const [modelsSource, setModelsSource] = useState("");
  const [modelsLoading, setModelsLoading] = useState(false);

  /*
    Reasoning effort is per adapter, not a shared scale: Claude and Codex reach
    "Extra high", Antigravity stops at "High", and OpenCode has no such setting
    at all. The server sends each adapter's own list, so the control is built
    from that rather than from a constant here — and it disappears entirely for
    an adapter with an empty list.
  */
  const efforts = useMemo(
    () => adapters.find((a) => a.id === adapter)?.efforts || [],
    [adapters, adapter]
  );

  const [isValidating, setIsValidating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [verdict, setVerdict] = useState(null); // {ok,message,...}

  const loadModels = useCallback(
    async (adapterId, preferredModel) => {
      if (!adapterId) return;
      setModelsLoading(true);
      setModels([]);
      setModelsSource("");
      try {
        const res = await getAiBuilderAdapterModels(adapterId);
        const list = res?.models || [];
        setModels(list);
        setModelsSource(res?.source || "");
        // Keep the current model if it is still offered, else the default/first.
        const keep = list.some((m) => m.id === preferredModel)
          ? preferredModel
          : (list.find((m) => m.default) || list[0])?.id || "";
        setModel(keep);
      } catch (err) {
        setModels([]);
        toast.error("Could not load models for this adapter.");
      } finally {
        setModelsLoading(false);
      }
    },
    [getAiBuilderAdapterModels]
  );

  useEffect(() => {
    getAiBuilderAdapterState()
      .then(async (state) => {
        setAdapters(state?.available || []);
        setEngine(state?.engine || "");
        setActiveAdapter(state?.active_adapter || "");
        setActiveModel(state?.active_model || "");
        setActiveEffort(state?.active_effort || "");
        setAdapter(state?.active_adapter || "");
        setEffort(state?.active_effort || "");
        await loadModels(state?.active_adapter, state?.active_model);
      })
      .catch(() => toast.error("Could not load the builder adapter settings."))
      .finally(() => setIsLoading(false));
  }, [getAiBuilderAdapterState, loadModels]);

  const onAdapterChange = (nextAdapter) => {
    setAdapter(nextAdapter);
    setVerdict(null);
    /*
      Reset the effort with the adapter. Carrying it across would offer a level
      the new CLI may not accept — "Extra high" means nothing to Antigravity —
      and an invalid one is refused on save. Empty means the builder uses that
      adapter's own default.
    */
    const next = adapters.find((a) => a.id === nextAdapter)?.efforts || [];
    setEffort((next.find((e) => e.default) || next[0])?.id || "");
    loadModels(nextAdapter, "");
  };

  const onEffortChange = (nextEffort) => {
    setEffort(nextEffort);
    setVerdict(null);
  };

  const onModelChange = (nextModel) => {
    setModel(nextModel);
    setVerdict(null);
  };

  const onTest = async () => {
    setIsValidating(true);
    setVerdict(null);
    try {
      const v = await validateAiBuilderAdapter(adapter, model, effort);
      setVerdict(v);
      if (v?.ok) toast.success("Adapter is usable.");
      else toast.error(v?.message || "Adapter is not usable.");
    } catch (err) {
      const msg = err?.response?.data?.error?.message || "The test could not run.";
      setVerdict({ ok: false, message: msg });
      toast.error(msg);
    } finally {
      setIsValidating(false);
    }
  };

  const onSave = async (force = false) => {
    setIsSaving(true);
    try {
      const { status, data } = await saveAiBuilderAdapter(adapter, model, effort, force);
      if (status >= 200 && status < 300 && data?.saved) {
        setActiveAdapter(data.active_adapter);
        setActiveModel(data.active_model);
        setActiveEffort(data.active_effort || "");
        setEffort(data.active_effort || "");
        setVerdict(data.verdict || { ok: true, message: "Saved." });
        toast.success(`Now using ${labelFor(data.active_adapter)} · ${data.active_model}.`);
      } else if (status === 409) {
        // Refused because the health check failed. Offer to force.
        setVerdict(data?.verdict || null);
        const msg = data?.error?.message || "The adapter is not usable.";
        toast.error(msg);
      } else {
        toast.error(data?.error?.message || "Could not save the selection.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.error?.message || "Could not save the selection.");
    } finally {
      setIsSaving(false);
    }
  };

  const labelFor = (id) => adapters.find((a) => a.id === id)?.label || id;

  const isDirty =
    adapter !== activeAdapter || model !== activeModel || effort !== activeEffort;
  const showForce = verdict && verdict.ok === false;

  if (isLoading) {
    return (
      <div className="mt-8 flex items-center gap-3 text-gray-500">
        <PulseLoader size={10} /> <span>Loading builder settings…</span>
      </div>
    );
  }

  return (
    <div className="mt-10 max-w-[560px]">
      <h2 className="text-[22px] font-semibold">AI Website Builder</h2>
      <p className="mt-1 text-sm text-gray-500">
        Choose which coding-agent CLI, model and reasoning effort build customer
        websites. Test a choice before saving — saving is blocked if the CLI is
        not installed or signed in.
      </p>

      <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm">
        <span className="text-gray-500">Currently live:</span>{" "}
        <strong>{labelFor(activeAdapter)}</strong>
        <span className="text-gray-400"> · </span>
        <strong>{activeModel}</strong>
        {activeEffort ? (
          <>
            <span className="text-gray-400"> · </span>
            <span className="text-gray-600">effort: {activeEffort}</span>
          </>
        ) : null}
        {engine && engine !== "cli" ? (
          <div className="mt-1 text-xs text-amber-600">
            Note: the builder engine is set to “{engine}”, not “cli”. This adapter
            is used only when the CLI engine is active.
          </div>
        ) : null}
      </div>

      <label className="mt-6 block text-sm font-medium text-gray-700">Adapter</label>
      <Select
        className="mt-1"
        value={adapter}
        onChange={(e) => onAdapterChange(e.target.value)}
      >
        {adapters.map((a) => (
          <option key={a.id} value={a.id}>
            {a.label}
          </option>
        ))}
      </Select>

      <div className="mt-4 flex items-center gap-2">
        <label className="block text-sm font-medium text-gray-700">Model</label>
        {modelsLoading ? (
          <span className="inline-flex items-center gap-1.5 text-xs text-indigo-600">
            <PulseLoader size={5} color="#4f46e5" />
            Loading models…
          </span>
        ) : modelsSource ? (
          <span className="text-xs font-normal text-gray-400">
            ({modelsSource === "live" ? "live from CLI" : "built-in list"}
            {models.length ? ` · ${models.length}` : ""})
          </span>
        ) : null}
      </div>
      <Select
        className="mt-1"
        value={model}
        onChange={(e) => onModelChange(e.target.value)}
        isDisabled={modelsLoading || models.length === 0}
        placeholder={modelsLoading ? "Loading models…" : undefined}
        opacity={modelsLoading ? 0.6 : 1}
      >
        {models.map((m) => (
          <option key={m.id} value={m.id}>
            {m.label || m.id}
          </option>
        ))}
      </Select>

      {efforts.length > 0 ? (
        <>
          <label className="mt-4 block text-sm font-medium text-gray-700">
            Reasoning effort
          </label>
          <Select
            className="mt-1"
            value={effort}
            onChange={(e) => onEffortChange(e.target.value)}
          >
            {efforts.map((e) => (
              <option key={e.id} value={e.id}>
                {e.label}
                {e.default ? " (default)" : ""}
              </option>
            ))}
          </Select>
          <p className="mt-1 text-xs text-gray-500">
            How hard the model thinks before it writes. Higher is slower and
            costs more per build, and each CLI has its own scale — this list is
            what {labelFor(adapter)} actually accepts.
          </p>
        </>
      ) : (
        <p className="mt-4 text-xs text-gray-500">
          {labelFor(adapter)} has no reasoning-effort setting; its provider
          decides.
        </p>
      )}

      {verdict ? (
        <div
          className={`mt-4 rounded-lg border px-4 py-3 text-sm ${
            verdict.ok
              ? "border-green-200 bg-green-50 text-green-800"
              : "border-red-200 bg-red-50 text-red-800"
          }`}
        >
          <div className="font-medium">
            {verdict.ok ? "Usable" : "Not usable"}
          </div>
          <div className="mt-0.5">{verdict.message}</div>
          {typeof verdict.installed === "boolean" ? (
            <div className="mt-1 text-xs opacity-80">
              installed: {String(verdict.installed)} · authenticated:{" "}
              {String(verdict.authenticated)}
              {verdict.version ? ` · ${verdict.version}` : ""}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="mt-6 flex items-center gap-3">
        <button
          type="button"
          onClick={onTest}
          disabled={isValidating || isSaving || !adapter}
          className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium hover:bg-gray-100 disabled:opacity-50"
        >
          {isValidating ? "Testing…" : "Test adapter"}
        </button>

        <button
          type="button"
          onClick={() => onSave(false)}
          disabled={isSaving || isValidating || !adapter || !model || !isDirty}
          className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {isSaving ? "Saving…" : "Save & activate"}
        </button>

        {showForce ? (
          <button
            type="button"
            onClick={() => onSave(true)}
            disabled={isSaving}
            className="rounded-md border border-red-300 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
            title="Save even though the health check did not pass"
          >
            Save anyway
          </button>
        ) : null}

        {isDirty ? (
          <span className="text-xs text-amber-600">Unsaved change</span>
        ) : null}
      </div>
    </div>
  );
}

export default AIBuilderAdapter;
